import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// add feature flags here
const FEATURE_FLAGS_INITIAL_STATE = {
  'ff-example': true,
  'ff-rp-decouple': true,
  'ff-monthly-invoices': true,
  'ff-product-content-update': true
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS_INITIAL_STATE;

// Feature flags interface
interface FeatureFlagState {
  flags: Record<FeatureFlagKey, boolean>;

  // Actions
  getAllFlagNames: () => FeatureFlagKey[];
  enableFlag: (flagName: FeatureFlagKey) => void;
  disableFlag: (flagName: FeatureFlagKey) => void;
  isFlagEnabled: (flagName: FeatureFlagKey) => boolean;
}

// Feature flag store with local storage persistence
export const useFeatureFlagStore = create<FeatureFlagState>()(
  persist(
    (set, get) => ({
      // Initial state
      flags: FEATURE_FLAGS_INITIAL_STATE,

      // Get all flag names
      getAllFlagNames: () => Object.keys(get().flags) as FeatureFlagKey[],

      // Enable a specific flag
      enableFlag: (flagName: FeatureFlagKey) =>
        set((state) => ({
          flags: { ...state.flags, [flagName]: true }
        })),

      // Disable a specific flag
      disableFlag: (flagName: FeatureFlagKey) =>
        set((state) => ({
          flags: { ...state.flags, [flagName]: false }
        })),

      // Check if a flag is enabled (returns false if flag doesn't exist)
      isFlagEnabled: (flagName: FeatureFlagKey) => {
        const state = get();
        return state.flags[flagName] === true;
      }
    }),
    {
      name: 'feature-flags-storage', // Local storage key
      version: 1 // Version for migrations if needed
    }
  )
);

export default useFeatureFlagStore;
